import { ErrorModal, HelmetDexteel, MesfModal } from "@dexteel/mesf-core";
import {
	Alert,
	Autocomplete,
	Button,
	CircularProgress,
	type DialogProps,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useShelfMovePreview } from "../hooks/useShelfMovePreview";
import type { Shelf } from "../models/Shelf";
import { moveShelf } from "../repositories/ShelvesRepository";

export type MovedShelf = {
	message: string;
	treeId: number;
	newParentTreeId: number;
};

type Props = {
	/** The shelf or group to move; null keeps the dialog closed. */
	shelf: Shelf | null;
	shelves: Shelf[];
	/** Destination already picked, when the dialog opens from a drag and drop. */
	initialDestinationId?: number | null;
	onHide: (moved?: MovedShelf) => void;
};

const keyOf = (node: Shelf | undefined | null) =>
	(node?.UniqueKey ?? "").trim();

export const MoveShelf = ({
	shelf,
	shelves,
	initialDestinationId = null,
	onHide,
}: Props) => {
	const maxWidth: DialogProps["maxWidth"] = "sm";
	const open = !!shelf;
	const [destinationId, setDestinationId] = useState<number | null>(null);
	// null takes the code the server suggests; anything typed replaces it
	const [codeInput, setCodeInput] = useState<string | null>(null);
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const queryClient = useQueryClient();

	useEffect(() => {
		// Cleared on close, so reopening never asks for the previous destination
		setDestinationId(open ? initialDestinationId : null);
		setCodeInput(null);
	}, [open, initialDestinationId]);

	const isGroup = keyOf(shelf).length === 3;
	const codeIsValid =
		codeInput === null ||
		(isGroup ? /^[A-Z]$/.test(codeInput) : /^\d{2}$/.test(codeInput));

	// A shelf goes into a group of shelves, a group into a top-level branch
	const destinations = useMemo(
		() =>
			shelves
				.filter((node) =>
					isGroup
						? node.ParentTreeId === null && keyOf(node).length === 2
						: keyOf(node).length === 3,
				)
				.filter((node) => node.TreeId !== shelf?.ParentTreeId)
				.sort((a, b) => keyOf(a).localeCompare(keyOf(b))),
		[shelves, shelf, isGroup],
	);

	const { data, isFetching } = useShelfMovePreview(
		shelf?.TreeId ?? null,
		destinationId,
		codeInput,
		open && codeIsValid,
	);

	// keepPreviousData can still hold the answer for another destination or code
	const summary =
		data?.summary &&
		data.summary.NewParentTreeId === destinationId &&
		(codeInput === null || data.summary.Code === codeInput)
			? data.summary
			: null;
	const previousSummary =
		data?.summary?.NewParentTreeId === destinationId ? data.summary : null;
	// The code only matters when the node can move, that is when it holds no documents
	const showCode =
		!!previousSummary &&
		previousSummary.Documents === 0 &&
		previousSummary.Code !== null;

	const onSubmit = async () => {
		if (!shelf?.TreeId || destinationId === null || !summary) return;
		setIsSubmitLoading(true);
		try {
			// The code and key shown, so a tree changed in between is refused rather than applied elsewhere
			await moveShelf(
				shelf.TreeId,
				destinationId,
				summary.Code,
				true,
				summary.NewKey,
			);
			onHide({
				message: `${summary.NodeTitle} was moved to ${summary.NewParentTitle} as ${summary.NewKey}`,
				treeId: shelf.TreeId,
				newParentTreeId: destinationId,
			});
		} catch (e) {
			setError((e as Error).message);
			queryClient.invalidateQueries(["shelves", "movePreview"]);
		}
		setIsSubmitLoading(false);
	};

	return (
		<>
			{open && <HelmetDexteel title="Move Shelf - Shelves" />}
			<MesfModal
				title="MOVE SHELF"
				open={open}
				maxWidth={maxWidth}
				handleClose={() => onHide()}
			>
				<MesfModal.Content dividers>
					<Grid2 container spacing={2}>
						<Grid2 size={12}>
							<Typography>
								Move <b>{shelf?.Title}</b>
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Now in{" "}
								{shelves.find((node) => node.TreeId === shelf?.ParentTreeId)
									?.Title ?? "the top level"}
							</Typography>
						</Grid2>
						<Grid2 size={12}>
							<Autocomplete
								options={destinations}
								value={
									destinations.find((node) => node.TreeId === destinationId) ??
									null
								}
								onChange={(_, value) => {
									setDestinationId(value?.TreeId ?? null);
									setCodeInput(null);
								}}
								getOptionLabel={(option) => option.Title}
								isOptionEqualToValue={(option, value) =>
									option.TreeId === value.TreeId
								}
								renderInput={(params) => (
									<TextField
										{...params}
										label={isGroup ? "Move into branch" : "Move into group"}
										margin="dense"
										autoFocus
									/>
								)}
							/>
						</Grid2>

						{showCode && (
							<Grid2 size={12}>
								<TextField
									label={
										isGroup ? "New code (one letter)" : "New code (two digits)"
									}
									margin="dense"
									autoComplete="off"
									value={codeInput ?? previousSummary?.Code ?? ""}
									onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
									error={!codeIsValid}
									helperText={
										codeIsValid
											? `New key: ${keyOf(destinations.find((node) => node.TreeId === destinationId))}${codeInput ?? previousSummary?.Code ?? ""}`
											: isGroup
												? "One letter, like G"
												: "Two digits, like 03"
									}
									slotProps={{ htmlInput: { maxLength: isGroup ? 1 : 2 } }}
								/>
							</Grid2>
						)}

						{destinationId !== null &&
							codeIsValid &&
							!summary &&
							isFetching && (
								<Grid2 size={12} sx={{ textAlign: "center" }}>
									<CircularProgress size="1.5rem" />
								</Grid2>
							)}

						{summary && !summary.IsValid && (
							<Grid2 size={12}>
								<Alert severity="warning">{summary.Reason}</Alert>
							</Grid2>
						)}

						{summary?.IsValid && (
							<Grid2 size={12}>
								<Alert severity="info">
									{summary.IsGroup
										? "Its shelves hold no documents, so the codes change to match the new place:"
										: "It holds no documents, so its code changes to match the new place:"}
									{data?.nodes.map((node) => (
										<Typography key={node.TreeId} variant="body2">
											{node.OldTitle} → <b>{node.NewTitle}</b>
										</Typography>
									))}
								</Alert>
							</Grid2>
						)}
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions>
					<Grid2 justifyContent="flex-end" size={12} width={"100%"}>
						<Grid2 container justifyContent="flex-end" spacing={1}>
							<Grid2 size={{ md: 3, xs: 5 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide()}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 5 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading && <CircularProgress size="1rem" />
									}
									disabled={isSubmitLoading || isFetching || !summary?.IsValid}
									variant="contained"
									color="primary"
									onClick={onSubmit}
								>
									Move
								</Button>
							</Grid2>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
